import {
  Checkbox,
  RadioGroup,
  RadioGroupItem,
  Typography,
} from "@suraasa/placebo-ui"
import {
  AssessmentQuestion,
  Question as StandaloneQuestion,
  QuestionType,
  UserResponse,
} from "api/resources/assessments/types"
import clsx from "clsx"

import FillBlanks from "./FillBlanks"
import QuestionHeader from "./QuestionHeader"

const Question = ({
  toggleMarkForReview,
  questionNumberOverride,
  question,
  handleMarkAnswer,
}: {
  questionNumberOverride?: string
  question: AssessmentQuestion
  toggleMarkForReview: (question: AssessmentQuestion) => void
  handleMarkAnswer: (
    question: StandaloneQuestion,
    newResponse: UserResponse,
    options?: {
      caseStudyId?: string
    }
  ) => void
}) => {
  const handleResponseChange = (option: number) => {
    if (question.questionType === QuestionType.CASE_STUDY)
      throw new Error("Trying to mark answer on a case study root")

    if (question.questionType === QuestionType.SINGLE_CORRECT) {
      if (question.response?.includes(option)) {
        handleMarkAnswer(question, [])
      } else {
        handleMarkAnswer(question, [option])
      }
    }

    if (question.questionType === QuestionType.MULTIPLE_CORRECT) {
      if (question.response?.includes(option)) {
        handleMarkAnswer(
          question,
          question.response.filter(x => x !== option)
        )
      } else {
        handleMarkAnswer(question, [...(question.response ?? []), option])
      }
    }
  }

  if (question.questionType === QuestionType.CASE_STUDY)
    return (
      <div className="mb-2.5 overflow-auto sm:overflow-visible">
        <QuestionHeader
          question={question}
          toggleMarkForReview={toggleMarkForReview}
        />

        <Typography className="whitespace-pre-line">{question.text}</Typography>

        <div className={clsx("flex flex-col ps-2 pt-3")}>
          {question.questions.map((q, index) => (
            <Question
              toggleMarkForReview={toggleMarkForReview}
              questionNumberOverride={`${question.questionNumber}.${index + 1}`}
              question={q}
              handleMarkAnswer={(q, r) => {
                handleMarkAnswer(q, r, { caseStudyId: question.id })
              }}
              key={q.id}
            />
          ))}
        </div>
      </div>
    )

  return (
    <div className="mb-2.5 overflow-visible">
      <QuestionHeader
        toggleMarkForReview={toggleMarkForReview}
        question={question}
        questionNumberOverride={questionNumberOverride}
      />

      {question.questionType === QuestionType.FILL_IN_THE_BLANKS ? (
        <FillBlanks
          question={question}
          onChange={(_, response) => handleMarkAnswer(question, response)}
        />
      ) : (
        <>
          <Typography className="whitespace-pre-line">
            {question.question.replaceAll("__blank__", "__________")}
          </Typography>
          <Typography
            variant="strongSmallBody"
            className="pt-2 text-onSurface-500"
          >
            Options
          </Typography>

          <span className={clsx("flex max-w-[605px] flex-col py-1.5 ")}>
            {question.questionType === QuestionType.SINGLE_CORRECT ? (
              <RadioGroup
                key={question.id}
                onValueChange={val => handleResponseChange(Number(val))}
              >
                {Object.entries(question.options).map(([value, label]) => {
                  const optionValue = Number(value)
                  const isSelected = question.response?.includes(optionValue)

                  return (
                    <label
                      key={optionValue}
                      className={clsx(
                        "flex cursor-pointer flex-row items-center gap-1 rounded-lg p-1 text-smallBody",
                        {
                          "bg-primary-100 border-2 border-primary-500":
                            isSelected,
                          "border-2 border-secondary-400": !isSelected,
                        }
                      )}
                      htmlFor={label}
                    >
                      <RadioGroupItem
                        value={optionValue.toString()}
                        checked={isSelected}
                        color="primary"
                        id={label}
                      />
                      <div>{label}</div>
                    </label>
                  )
                })}
              </RadioGroup>
            ) : (
              Object.entries(question.options).map(([value, label]) => {
                const optionValue = Number(value)
                const isSelected = question.response?.includes(optionValue)

                return (
                  <label
                    key={optionValue}
                    htmlFor={label}
                    className={clsx(
                      "mb-1.5 flex cursor-pointer flex-row items-center gap-1 rounded-lg p-1 text-smallBody",
                      {
                        "bg-primary-100 border-2 border-primary-500":
                          isSelected,
                        "border-2 border-secondary-400": !isSelected,
                      }
                    )}
                  >
                    <Checkbox
                      onCheckedChange={() => handleResponseChange(optionValue)}
                      color="primary"
                      id={label}
                      checked={isSelected}
                    />
                    <div>{label}</div>
                  </label>
                )
              })
            )}
          </span>
        </>
      )}
    </div>
  )
}

export default Question
